import type { StoryEditAction } from 'common/story-edit-operations/types';
import { generateId } from 'utils/generate-id';
import { toast } from 'admin/components/common/MessageContainer/toast';
import type { EditCaptureResult } from './save-capture';
import { storyEditSaverFor } from './saver';

// Filter the browser console by this prefix to follow every captured action.
const LOG_PREFIX = '[edit-operations]';

/** Which editor's autosave the action belongs to; console logs only, never sent. */
export type EditCaptureSource = 'card-editor' | 'story-editor';

/** Logs that a write reaching autosave was deliberately left out of capture. */
export function logExcludedEditCapture(source: EditCaptureSource, details?: object): void {
	console.info(`${LOG_PREFIX} ${source}: excluded from capture`, details ?? {});
}

/** Logs a load normalization that waits for the first edit, which sends it ahead of its own changes. */
export function logHeldNormalization(
	source: EditCaptureSource,
	details: { action: string; changeCount: number }
): void {
	console.info(`${LOG_PREFIX} ${source}: normalization held until the first edit`, details);
}

/**
 * Queues the captured action on the story's saver; local editing never waits for delivery.
 * Every outcome is logged, so a write without a queued action is never silent.
 */
export function deliverEditCapture(
	capture: EditCaptureResult,
	{ storyId, source, action: actionName }: { storyId: string; source: EditCaptureSource; action: string }
): void {
	if (capture.status === 'noop') {
		console.info(`${LOG_PREFIX} ${source}: no authored changes`, { action: actionName });
		return;
	}
	if (capture.status === 'unsupported') {
		console.warn(`${LOG_PREFIX} ${source}: action was not captured`, {
			action: actionName,
			reason: capture.reason,
		});
		// Not expected in practice, so it is shown in the UI too instead of passing unnoticed in the console.
		toast.error(`Edit operations: the change was not captured. ${capture.reason}`);
		return;
	}

	const action: StoryEditAction = { actionId: generateId(), changes: capture.changes };
	const saver = storyEditSaverFor(storyId);
	if (!saver) {
		// Only the edit-operations save mechanism creates savers; nothing else delivers actions.
		console.warn(`${LOG_PREFIX} ${source}: edit operations were not queued`, {
			storyId,
			actionId: action.actionId,
			status: 'delivery_failed',
		});
		return;
	}
	console.info(`${LOG_PREFIX} ${source}: queued`, { storyId, action: actionName, editAction: action });
	saver.enqueue(action);
}
